import { Market, Region } from 'clickNgo/configs/geo'
import { guid } from 'core/classes/helpers/GuidHelper'

import { IPartner } from '../models'

export enum PartnerPagePathPiece {
  Search = 'search',
  PartnerDetailsNew = 'partner-details',
  PartnerDetails = 'partner-dashboard',
  BasicException = 'partner-exception',
  P360 = 'jbl-hpscoutso',
  GlobalPartner = 'jbl-hpscoutso-global',
}

export enum PartnerWorkflowStatus {
  REJECTED = 'Rejected',
  DONE = 'Done',
  WIP = 'WIP',
  DRAFT = 'Draft',
  NEW = 'New',
  APPROVED = 'Approved',
  AWAITING = 'Awaiting Contracts Team Approval',
  NONE = '',
}

export enum PartnerHierarchyType {
  HEADQUARTERS = 'Headquarters',
  BRANCH = 'Branch',
  AFFILIATE = 'Affiliate',
  ONLINE = 'Online',
  SUBSIDIARY = 'Subsidiary',
  RESELLER_NETWORK = 'Reseller Network',
}

export enum PartnerStatus {
  Active = 'Active',
  Inactive = 'Inactive',
  Activating = 'Activating',
  Deactivating = 'Deactivating',
  Compliance = 'Compliance Review',
  New = 'New',
  Unknown = 'Unknown',
}

export enum PartnerType {
  Silver = 'Silver',
  BusinessPartner = 'Business Partner',
  RetailPowerCDR = 'Retail Power CDR',
  SuppliesChannelDistribution = 'Supplies Channel Distribution',
  RetailSynergy = 'Retail Synergy',
  Synergy = 'Synergy',
  PowerServices = 'Power Services',
  RetailPower = 'Retail Power',
  ProximityWithoutContractOrUnmanaged = 'Proximity Without Contract/Unmanaged',
  DummyPartner = 'Dummy Partner',
  OtherReseller = 'Other Reseller',
  SuppliesDistributor = 'Supplies Distributor',
  Power = 'Power',
  Distributor = 'Distributor',
  AffiliatePartner = 'Affiliate Partner',
  MarketingAgency = 'Marketing Agency',
  RetailPowerX = 'Retail PowerX',
  Gold = 'Gold',
  SubDistributor = 'Sub Distributor',
  OtherContractedPartner = 'Other Contracted Partner',
  OtherCorporateReseller = 'Other Corporate Reseller',
  PowerCDR = 'Power CDR',
  Platinum = 'Platinum',
}

export enum EditPartnerRequestType {
  BASIC = 'basic',
  LEGAL = 'legal',
}

export enum DummyPartnerIdPrefix {
  PORTAL = 'portal-',
  NEW = 'new-',
}

export const marketWithOptionalURL: string[] = [
  Market.GREATER_ASIA,
  Market.GREATER_CHINA,
  Market.INDIA_B_SL,
  Market.LATIN_AMERICA,
]

export const asianCountriesWithEspeciallyRequirements = [
  'China',
  'Japan',
  'Taiwan',
  'Korea (Rep.)',
  'Thailand',
  'Vietnam',
]

export const countryWithOptionalURL = [
  'Albania',
  'Algeria',
  'Angola',
  'Armenia',
  'Bahrain',
  'Belarus',
  'Benin',
  'Bosnia and Herzegovina',
  'Botswana',
  'Burkina Faso',
  'Cameroon',
  'Congo Democratic Republic',
  'Côte d Ivoire',
  'Egypt',
  'Ethiopia',
  'French Guiana',
  'French Polynesia',
  'Gabon',
  'Gambia',
  'Ghana',
  'Guadeloupe',
  'Iraq',
  'Kazakhstan',
  'Kenya',
  'Kosovo',
  'Kuwait',
  'Lebanon',
  'Lesotho',
  'Liberia',
  'Madagascar',
  'Martinique',
  'Mauritania',
  'Mayotte',
  'Moldavia',
  'Montenegro',
  'Morocco',
  'Mozambique',
  'Namibia',
  'Nigeria',
  'Oman',
  'Qatar',
  'Reunion',
  'Senegal',
  'Serbia And Montenegro',
  'Serbia',
  'South Africa',
  'Swaziland',
  'Tanzania United Republic',
  'Tunisia',
  'Uganda',
  'Yemen',
  'Zimbabwe',
]

export const PARTNER_COUNTRY_WITH_ENGLISH_NAME = [
  'China',
  'Japan',
  'Taiwan',
  'Korea',
  'Thailand',
  'Vietnam',
]

export enum PartnerExceptionReasonKey {
  NotAllowed = 'not-allowed',
  NotFound = 'not-found',
  IncorrectMarket = 'incorrect-market',
  UnavailableGlobalPartner = 'unavailable-global-partner',
  UndefinedError = 'undefined-error',
}

const PARTNER_COMMENTS_CHAT_SOURCE_PREFIX = 'partner_dashboard_comments_'

/** Chat source key for the Comments section on Partner Details. */
export const getPartnerCommentsChatSource = (partnerId: string): string =>
  `${PARTNER_COMMENTS_CHAT_SOURCE_PREFIX}${partnerId}`

export const initialPartnerData: IPartner = {
  accreditations: '',
  addressLine1: '',
  addressLine2: '',
  addressLine3: '',
  altVendorPC: '',
  altVendorPrint: '',
  amplifyImpactCountOfStars: 0,
  amplifyImpactPledgeType: 'CATALYST',
  approverComment: '',
  backupContacts: [],
  bankAccount: '',
  bankAccountName: '',
  bankName: '',
  city: '',
  communicationName: '',
  companyDomain: '',
  companyEnglishName: '',
  companyLegalName: '',
  companyLegalNameNonLatin: '',
  completeness: 0,
  country: '',
  countryCode: '',
  coverageNumber: '',
  coverageUnit: '',
  description: '',
  eCommerce: '',
  eCommerceDetails: '',
  ecommerceLinks: [],
  email: '',
  entryId: 0,
  entryId458: 0,
  entryId406: 0,
  facebookLink: '',
  gallery: '',
  hasAgreements: false,
  hasActiveAgreement: false,
  hasNotOnlineCommerce: false,
  hierarchyType: PartnerHierarchyType.HEADQUARTERS,
  hours: '',
  hpdPartnerGuid: '',
  hpMemberSince: '',
  hpMemberUnderAmplifySince: '',
  image: '',
  isAmplifyImpactAssessmentSigned: false,
  isAmplifyImpactEligible: false,
  isAmplifyImpactMinOneDeal: false,
  isAmplifyImpactPledgeSigned: false,
  isAmplifyMember: false,
  isDormantDDQNotified: false,
  isAWDP: false,
  isBusinessPartner: false,
  isNewPartner: true,
  isPolyPartner: false,
  isQualified: false,
  isr: '',
  isTopPartner: false,
  lastUpdatedDate: '',
  lat: 0,
  legalAddressLine1: '',
  legalAddressLine2: '',
  legalAddressLine3: '',
  legalApproverComment: '',
  legalCity: '',
  legalCountry: '',
  entryId502: 0,
  legalModified: '',
  legalModifiedBy: '',
  legalModifiedValues: [],
  legalProfilingComment: '',
  legalRegion: Region.EMEA,
  legalState: '',
  legalWorkflowStatus: PartnerWorkflowStatus.NONE,
  legalZipCode: '',
  link: '',
  linkedInLink: '',
  lng: 0,
  locationId: '',
  loyaltyIndex: 0,
  mainContact: '',
  market: Market.NORTH_AMERICA,
  missedRoles: [],
  modified: '',
  modifiedAdd: '',
  modifiedBy: '',
  modifiedByAdd: '',
  modifiedValues: [],
  oldPartnerId: '',
  organization: '',
  parentLocationId: '',
  parentPartnerId: '',
  partnerAgrees: 0,
  partnerCommunication: '',
  partnerId: `new-${guid()}`,
  partnerSince: '',
  partnerType: '',
  partnerTypeStartDate: '',
  phone: '',
  phoneCountryCode: '',
  profileTeamComment: '',
  profileUpdateDate: '',
  programCodes: [],
  region: Region.EMEA,
  registrationNumber: 0,
  reporterId: [],
  resellerAbInd: '',
  responseTimeNumber: 0,
  responseTimeUnits: '',
  rplStatus: '',
  serviceCoverage: 0,
  shouldUpdateInMetalocator: false,
  socialMediaLinks: [],
  source: '',
  sourceRequester: '',
  splPriceFlag: '',
  splPriceOverride: '',
  stage: '',
  state: '',
  status: '',
  stocking: '',
  storeNumber: '',
  tagIdList: [],
  taxId: '',
  territoryExtensionConducted: 0,
  territoryExtensionsBusinessJustification: '',
  territoryExtensionsList: '',
  topPartnerId: '',
  twitterLink: '',
  url: '',
  whatsAppLink: '',
  workflowStatus: PartnerWorkflowStatus.NONE,
  zipCode: '',
  zipCodeCovered: 0,
  zipCodes: [],
  onboardingRequestId: null,
}
